import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'

interface Task {
  id: string
  user_request: string
  status: string
  plan?: any
  created_at: string
  completed_at?: string
}

interface Agent {
  id: string
  name: string
  description: string
  role: string
  model_recommended: string
  reasoning?: string
}

interface Discussion {
  id: string
  agent_name?: string
  message_type: string
  content: string
  created_at: string
  metadata?: any
}

interface Proposal {
  id: string
  agent_name?: string
  proposal_type: string
  content: string
  status: string
  created_at: string
}

function TaskView() {
  const { id } = useParams<{ id: string }>()
  const [task, setTask] = useState<Task | null>(null)
  const [agents, setAgents] = useState<Agent[]>([])
  const [discussions, setDiscussions] = useState<Discussion[]>([])
  const [proposals, setProposals] = useState<Proposal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (id) {
      fetchTaskData()
    }
  }, [id])

  const fetchTaskData = async () => {
    try {
      const response = await fetch(`/api/tasks/${id}`)
      if (!response.ok) throw new Error('Failed to fetch task')
      const data = await response.json()
      setTask(data.task)
      setAgents(data.agents)
      setDiscussions(data.discussions)
      setProposals(data.proposals)
    } catch (err) {
      setError('Failed to load task')
    } finally {
      setLoading(false)
    }
  }

  const getMessageEmoji = (type: string) => {
    const emojis: Record<string, string> = {
      'proposal': '💡',
      'question': '❓',
      'answer': '💬',
      'decision': '✅',
      'system': '⚙️',
      'thought': '💭',
      'result': '📊'
    }
    return emojis[type] || '💬'
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'accepted': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Carregando...</div>
      </div>
    )
  }

  if (error || !task) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error || 'Task not found'}</p>
        <Link to="/" className="text-primary-600 hover:underline mt-2 inline-block">
          ← Voltar ao Dashboard
        </Link>
      </div>
    )
  }

  return (
    <div>
      <Link to="/" className="text-primary-600 hover:underline mb-4 inline-block">
        ← Voltar ao Dashboard
      </Link>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Tarefa</h1>
        <p className="text-gray-600">{task.user_request}</p>
        <div className="flex items-center space-x-4 mt-4 text-sm text-gray-500">
          <span className="px-2 py-1 bg-gray-100 rounded">Status: {task.status}</span>
          <span>Criada: {new Date(task.created_at).toLocaleString()}</span>
          {task.completed_at && (
            <span>Concluída: {new Date(task.completed_at).toLocaleString()}</span>
          )}
        </div>
      </div>

      {agents.length > 0 && (
        <div className="card mb-6">
          <h2 className="text-xl font-semibold mb-4">🤖 Agentes</h2>
          <div className="grid gap-4">
            {agents.map((agent) => (
              <div key={agent.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-lg">{agent.name}</h3>
                  <span className="text-sm text-gray-500">{agent.role}</span>
                </div>
                <p className="text-gray-600 mb-2">{agent.description}</p>
                <p className="text-sm text-gray-500">
                  Modelo: {agent.model_recommended}
                </p>
                {agent.reasoning && (
                  <p className="text-sm text-gray-400 mt-2">
                    Por que: {agent.reasoning}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {proposals.length > 0 && (
        <div className="card mb-6">
          <h2 className="text-xl font-semibold mb-4">💡 Propostas</h2>
          <div className="grid gap-4">
            {proposals.map((proposal) => (
              <div key={proposal.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{proposal.agent_name || 'Sistema'}</span>
                  <span className={`px-2 py-1 rounded text-xs ${getStatusColor(proposal.status)}`}>
                    {proposal.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mb-2">Tipo: {proposal.proposal_type}</p>
                <p className="text-gray-700">{proposal.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <h2 className="text-xl font-semibold mb-4">💬 Discussão</h2>
        {discussions.length === 0 ? (
          <p className="text-gray-500">Nenhuma mensagem ainda.</p>
        ) : (
          <div className="space-y-4">
            {discussions.map((discussion) => (
              <div key={discussion.id} className="flex items-start space-x-3">
                <span className="text-2xl">{getMessageEmoji(discussion.message_type)}</span>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-medium text-sm">
                      {discussion.agent_name || 'Sistema'}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(discussion.created_at).toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{discussion.content}</p>
                  {discussion.metadata && (
                    <div className="text-xs text-gray-400 mt-1">
                      {discussion.metadata.tokens && (
                        <span>Tokens: {discussion.metadata.tokens} | </span>
                      )}
                      {discussion.metadata.cost && (
                        <span>Custo: ${discussion.metadata.cost.toFixed(4)}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default TaskView
